import { Button, IconButton, Switch, TimePicker, useSnackbar } from '@hue/design-system';
import { Plus, Trash2 } from 'lucide-react';
import {
  weekdays,
  openingHoursError,
  type OpeningHourDraft,
} from './business-unit-opening-hours-model.js';

const MAX_PER_DAY = 3;

/// Horário de funcionamento do estabelecimento (ADR 0074): um dia fechado não tem faixa.
export function BusinessUnitOpeningHours({
  value,
  onChange,
  error,
}: {
  value: OpeningHourDraft[];
  onChange: (value: OpeningHourDraft[]) => void;
  error?: string | undefined;
}) {
  const snackbar = useSnackbar();
  function copyDay(weekday: number) {
    const day = value.filter((draft) => draft.weekday === weekday);
    onChange(
      weekdays.flatMap(({ value: target }) => day.map((draft) => ({ ...draft, weekday: target }))),
    );
    snackbar.success('Funcionamento copiado para os demais dias. Salve a unidade para aplicar.');
  }

  function replaceDay(weekday: number, day: Array<Omit<OpeningHourDraft, 'weekday'>>) {
    onChange([
      ...value.filter((draft) => draft.weekday !== weekday),
      ...day.map((draft) => ({ ...draft, weekday })),
    ]);
  }

  return (
    <fieldset className="grid gap-3">
      <legend className="mb-1 text-sm font-semibold">
        Horário de funcionamento{' '}
        <span className="font-normal text-content-muted">· aparece no portal do cliente</span>
      </legend>
      <p className="text-xs leading-5 text-content-muted">
        Só informa o cliente. As vagas da agenda vêm do expediente de cada profissional. Copiar
        substitui as faixas dos outros seis dias, inclusive os fechados; você pode ajustá-los antes
        de salvar.
      </p>
      <ul className="grid gap-2">
        {weekdays.map(({ value: weekday, label }) => {
          const day = value.filter((draft) => draft.weekday === weekday);
          return (
            <li
              key={weekday}
              className="grid gap-2 rounded-xl border border-surface-border px-3 py-2 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start"
            >
              <Switch
                checked={day.length > 0}
                onChange={(open) =>
                  replaceDay(weekday, open ? [{ start: '09:00', end: '18:00' }] : [])
                }
              >
                {label}
              </Switch>
              {day.length === 0 ? (
                <p className="text-sm text-content-muted sm:pt-2">Fechado</p>
              ) : (
                <div className="grid gap-2">
                  {day.map((draft, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[minmax(0,1fr)_2.5rem] items-end gap-2"
                    >
                      <div className="grid min-w-0 gap-2 min-[360px]:grid-cols-2">
                        <TimePicker
                          label={`Abre (${label.toLowerCase()})`}
                          value={draft.start || null}
                          onValueChange={(start) =>
                            replaceDay(
                              weekday,
                              day.map((item, at) =>
                                at === index ? { ...item, start: start ?? '' } : item,
                              ),
                            )
                          }
                        />
                        <TimePicker
                          label={`Fecha (${label.toLowerCase()})`}
                          value={draft.end || null}
                          onValueChange={(end) =>
                            replaceDay(
                              weekday,
                              day.map((item, at) =>
                                at === index ? { ...item, end: end ?? '' } : item,
                              ),
                            )
                          }
                        />
                      </div>
                      {index === 0 ? (
                        <IconButton
                          label={`Adicionar faixa (${label.toLowerCase()})`}
                          icon={<Plus />}
                          disabled={day.length >= MAX_PER_DAY}
                          onClick={() =>
                            replaceDay(weekday, [...day, { start: day.at(-1)!.end, end: '' }])
                          }
                        />
                      ) : (
                        <IconButton
                          label={`Remover faixa (${label.toLowerCase()})`}
                          icon={<Trash2 />}
                          tone="danger"
                          onClick={() =>
                            replaceDay(
                              weekday,
                              day.filter((_, at) => at !== index),
                            )
                          }
                        />
                      )}
                    </div>
                  ))}
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-auto min-h-11 max-w-full whitespace-normal py-2"
                      aria-label={`Copiar funcionamento de ${label.toLowerCase()} para os demais dias`}
                      disabled={Boolean(openingHoursError(day))}
                      onClick={() => copyDay(weekday)}
                    >
                      Copiar funcionamento para os demais dias
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
